/**
 * `/download` — how somebody actually gets the Android build onto a phone.
 *
 * A bare .apk link is genuinely awkward on mobile: the browser warns, some browsers rename the
 * file, some show no progress at all, and the *install* is gated behind a permission the user
 * has to go and grant in Settings. None of that is a misconfiguration, and all of it is
 * survivable if somebody says it out loud first. That is what this page is for.
 *
 * ## Three things it says that the static page on the downloads host cannot
 *
 * `scripts/mobile/download-page.html` (served at `downloads.chompdome.com/android`) is
 * deliberately version-agnostic — it is published from the release workflow and must never go
 * stale, so it knows nothing about what it is linking. This page reads the release manifest, so
 * it can print the version, the size and the date, and it can link the **immutable versioned
 * key** rather than the overwritten pointer. The two pages are not duplicates of one another:
 * one lives on the bucket and always works, this one lives on the product site and is specific.
 *
 * ## It never offers a file the reader cannot install
 *
 * The APK is the lead action on Android only. On iOS there is no build to hand over and the
 * page says so rather than putting a button in front of it; on a desktop the browser game is
 * the lead and the .apk is a secondary link for somebody deliberately moving a file to a phone.
 *
 * ## No `download` attribute on the link, ever
 *
 * On Android it makes the browser save the file silently and SUPPRESSES the download-complete
 * "Open" prompt — the one tap that starts the install. The object's own `Content-Disposition`
 * already forces the download and sets the filename. Same rule, same reason, as the static page.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import {
  ANDROID_POINTER_URL,
  ANDROID_SHORT_URL,
  type AndroidRelease,
  apkUrl,
  currentDownloadPlatform,
  fetchAndroidRelease,
  formatApkSize,
  formatReleaseDate,
} from '@/lib/android-download';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/download')({
  component: DownloadPage,
});

function DownloadPage() {
  // Read once. The device does not change platform under the reader, and re-deciding it on
  // every render would be one `navigator` parse per keystroke elsewhere on the page.
  const [platform] = useState(currentDownloadPlatform);

  // One attempt, no retry: every failure here has the same answer — fall back to the pointer
  // key — and a reader waiting through three backoffs to be told nothing is a worse page.
  const release = useQuery({
    queryKey: ['android-release'],
    queryFn: ({ signal }) => fetchAndroidRelease(signal),
    retry: false,
  });

  const apk = release.data ? apkUrl(release.data) : ANDROID_POINTER_URL;

  return (
    <div className="flex flex-1 justify-center p-4">
      <div className="w-full max-w-2xl space-y-4 self-start">
        <Card>
          <CardHeader>
            <CardTitle>Chompdome for Android</CardTitle>
            <CardDescription>
              Installed directly rather than through the Play Store. The browser game is the same
              game — the app just keeps the screen awake, fills the display and starts faster.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ReleaseFacts release={release.data} pending={release.isPending} />
            {platform === 'android' ? (
              <AndroidCallToAction apk={apk} />
            ) : platform === 'ios' ? (
              <IosCallToAction />
            ) : (
              <DesktopCallToAction apk={apk} />
            )}
          </CardContent>
        </Card>

        {platform !== 'ios' && <InstallSteps />}
        {platform !== 'ios' && <Troubleshooting />}
      </div>
    </div>
  );
}

/**
 * Version, size and date — or an honest line saying they could not be read.
 *
 * The manifest is a separate host and a separate deploy, so "unavailable" is a normal state
 * rather than an error: the download below still works, it is just unlabelled.
 */
function ReleaseFacts({ release, pending }: { release?: AndroidRelease; pending: boolean }) {
  if (pending) {
    return <p className="text-sm text-muted-foreground">Looking up the latest build…</p>;
  }

  if (!release) {
    return (
      <p className="text-sm text-muted-foreground">
        The latest build's details could not be read just now. The download below is still the
        newest build.
      </p>
    );
  }

  return (
    <dl className="grid grid-cols-3 gap-2 text-sm">
      <Fact label="Version">
        {release.version}{' '}
        <span className="text-muted-foreground">(build {release.buildNumber})</span>
      </Fact>
      <Fact label="Size">{formatApkSize(release.sizeBytes)}</Fact>
      <Fact label="Released">{formatReleaseDate(release.releasedAt)} UTC</Fact>
    </dl>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

/** No `download` attribute — see the note at the top of this file. */
function AndroidCallToAction({ apk }: { apk: string }) {
  return (
    <div className="space-y-2">
      <a href={apk} className={cn(buttonVariants({ variant: 'accent', size: 'lg' }), 'w-full')}>
        Download the APK
      </a>
      <p className="text-center text-xs text-muted-foreground">
        Or open <code className="rounded bg-muted px-1 py-0.5">{ANDROID_SHORT_URL}</code> — it
        always redirects to the newest build.
      </p>
    </div>
  );
}

function IosCallToAction() {
  return (
    <div className="space-y-3">
      <p className="text-sm">
        <strong>There is no iOS download.</strong> Apple does not allow apps to be installed from
        the web, and Chompdome is not on the App Store yet — so there is no file here that your
        iPhone or iPad could open.
      </p>
      <Link to="/" className={cn(buttonVariants({ variant: 'accent', size: 'lg' }), 'w-full')}>
        Play in your browser
      </Link>
      <p className="text-sm text-muted-foreground">
        The whole game runs in Safari, touch controls included. <strong>Share</strong> →{' '}
        <strong>Add to Home Screen</strong> gives it an icon and a full-screen window, which is most
        of what the app would have added.
      </p>
    </div>
  );
}

function DesktopCallToAction({ apk }: { apk: string }) {
  return (
    <div className="space-y-3">
      <Link to="/" className={cn(buttonVariants({ variant: 'accent', size: 'lg' }), 'w-full')}>
        Play in your browser
      </Link>
      <p className="text-sm text-muted-foreground">
        You are on a desktop, so there is nothing here to install. To put it on a phone, open{' '}
        <code className="rounded bg-muted px-1 py-0.5">{ANDROID_SHORT_URL}</code> on the phone
        itself — or{' '}
        <a href={apk} className="text-primary underline underline-offset-4">
          download the .apk
        </a>{' '}
        and transfer it across.
      </p>
    </div>
  );
}

/**
 * The permission flow, spelled out.
 *
 * Step 3 is the one this section exists for. "Install unknown apps" is granted per *installing*
 * app — the browser, or the file manager — not once for the device, so somebody who downloaded
 * in Chrome and then opened the file from Files is asked again, about a different app, and
 * reads it as the install having failed.
 */
function InstallSteps() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Installing it</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="list-decimal space-y-3 pl-5 text-sm">
          <li>
            Tap <strong>Download the APK</strong>. Android warns that this kind of file{' '}
            <em>can harm your device</em> — choose <strong>Download anyway</strong>. Every
            sideloaded app shows this.
          </li>
          <li>
            When it finishes, tap <strong>Open</strong> in the notification. If you miss it, the
            file is in <strong>Downloads</strong>.
          </li>
          <li>
            The first time, Android refuses and offers <strong>Settings</strong>. That opens{' '}
            <strong>Install unknown apps</strong> for whichever app you are installing from — turn
            on <strong>Allow from this source</strong>, press back, and tap the file again. The
            permission is per app, so downloading in one browser and opening from another asks
            twice.
          </li>
          <li>
            Tap <strong>Install</strong>, then <strong>Open</strong>.
          </li>
          <li>
            Play Protect may offer to scan the app or say it was blocked. Choose{' '}
            <strong>Install anyway</strong> / <strong>More details</strong> — it is saying the app
            is not from the Play Store, which is true.
          </li>
        </ol>
      </CardContent>
    </Card>
  );
}

function Troubleshooting() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">If something looks wrong</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="list-disc space-y-3 pl-5 text-sm">
          <li>
            <strong>Nothing seems to be happening.</strong> Some browsers show no progress at all
            for an .apk. Check the notification shade and <strong>Downloads</strong> before tapping
            again — a second tap downloads a second copy.
          </li>
          <li>
            <strong>Updating.</strong> Install over the top; your account and settings are kept and
            you never need to uninstall first.
          </li>
          <li>
            <strong>"App not installed".</strong> Almost always an older copy signed with a
            different key. Uninstall that one and install this build again.
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}
